// Team administration (PLAN-P8) — a tenant admin lists members, invites by email + role, changes roles,
// and revokes access. The user registry + role assignments are owner-only, so this talks to the
// auth-worker (AUTH_BASE) credentialed, not the tenant-worker. Invited users have no email sent (X5
// gated) — they simply log in via WorkOS and are linked by email into this tenant.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "../lib/i18n";
import { useSession } from "../lib/session";
import { Can } from "../lib/rbac";
import { AUTH_BASE } from "../lib/env";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ROLES = ["controller_tenant_admin", "controller_tenant_user", "processor_tenant_admin", "processor_tenant_user"];

async function authFetch(path: string, init?: RequestInit): Promise<unknown> {
  const res = await fetch(`${AUTH_BASE}${path}`, {
    ...init, credentials: "include",
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  const body = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) throw new Error(typeof body === "object" && body && "error" in body ? String((body as Record<string, unknown>).error) : `HTTP ${res.status}`);
  return body;
}

export function TeamRoute() {
  const { t } = useI18n();
  const session = useSession();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["team"] });
  const { data, isLoading, error } = useQuery({ queryKey: ["team"], queryFn: () => authFetch("/tenant/users") as Promise<Array<Record<string, unknown>>> });
  const rows = data ?? [];

  return (
    <section>
      <PageHeader title={t("team.title")} description={t("team.intro")} />

      <Can perm="tenant.invite_user"><InviteForm onDone={invalidate} /></Can>

      {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      {!isLoading && !error && rows.length === 0 && <p className="text-sm text-muted-foreground">{t("team.empty")}</p>}

      {rows.length > 0 && (
        <div className="max-w-3xl rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>{[t("team.email"), t("team.role"), t("team.status"), ""].map((c, i) => <TableHead key={i}>{c}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const userId = String(r.userId);
                const isSelf = userId === session.user.id;
                return (
                  <TableRow key={userId}>
                    <TableCell>{String(r.email)}{r.fullName ? <span className="text-muted-foreground"> · {String(r.fullName)}</span> : null}{isSelf ? <span className="text-xs text-muted-foreground"> ({t("team.you")})</span> : null}</TableCell>
                    <TableCell>
                      <Can perm="tenant.invite_user" fallback={<span>{String(r.roleCode ?? "—")}</span>}>
                        <RoleSelect userId={userId} current={String(r.roleCode ?? "")} onDone={invalidate} />
                      </Can>
                    </TableCell>
                    <TableCell><Badge variant={String(r.status) === "active" ? "default" : "outline"}>{t(`team.${String(r.status)}`)}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      {!isSelf && <Can perm="tenant_user.revoke_user"><RevokeButton userId={userId} onDone={invalidate} /></Can>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function InviteForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [roleCode, setRoleCode] = useState(ROLES[1]!); // default: controller_tenant_user
  const mut = useMutation({
    mutationFn: () => authFetch("/tenant/users/invite", { method: "POST", body: JSON.stringify({ email: email.trim(), roleCode }) }),
    onSuccess: () => { setEmail(""); onDone(); },
  });
  return (
    <Card className="my-3 max-w-2xl">
      <CardHeader><CardTitle className="text-base">{t("team.invite")}</CardTitle></CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-2">
          <div className="grid flex-[1_1_240px] gap-1.5">
            <Label htmlFor="team-invite-email">{t("team.email")}</Label>
            <Input id="team-invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("team.email")} type="email" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="team-invite-role">{t("team.role")}</Label>
            <Select value={roleCode} onValueChange={setRoleCode}>
              <SelectTrigger id="team-invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !email.trim()}>{mut.isPending ? "…" : t("team.send")}</Button>
        </div>
        {mut.isError && <p className="mt-2 text-sm text-destructive">{(mut.error as Error).message}</p>}
        {mut.isSuccess && <p className="mt-2 text-sm text-emerald-600">{t("team.invited")}</p>}
      </CardContent>
    </Card>
  );
}

function RoleSelect({ userId, current, onDone }: { userId: string; current: string; onDone: () => void }) {
  const mut = useMutation({
    mutationFn: (roleCode: string) => authFetch("/tenant/users/role", { method: "PATCH", body: JSON.stringify({ userId, roleCode }) }),
    onSuccess: onDone,
  });
  return (
    <Select value={current} onValueChange={(v) => mut.mutate(v)} disabled={mut.isPending}>
      <SelectTrigger size="sm" aria-label="role"><SelectValue /></SelectTrigger>
      <SelectContent>
        {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function RevokeButton({ userId, onDone }: { userId: string; onDone: () => void }) {
  const { t } = useI18n();
  const [confirm, setConfirm] = useState(false);
  const mut = useMutation({ mutationFn: () => authFetch("/tenant/users/revoke", { method: "POST", body: JSON.stringify({ userId }) }), onSuccess: () => { setConfirm(false); onDone(); } });
  if (!confirm) return <Button variant="link" size="sm" className="h-auto px-1.5 text-destructive" onClick={() => setConfirm(true)}>{t("team.revoke")}</Button>;
  return <span className="text-sm">{t("crud.sure")} <Button variant="link" size="sm" className="h-auto px-1.5 text-destructive" disabled={mut.isPending} onClick={() => mut.mutate()}>{t("crud.yes")}</Button><Button variant="link" size="sm" className="h-auto px-1.5" onClick={() => setConfirm(false)}>{t("crud.no")}</Button></span>;
}
